import { useState } from "react";
import { Copy, Check, ArrowRight, BookOpen, Key, Settings, Eye, EyeOff } from "lucide-react";
import { saasAppUrl } from "@/lib/app";
import { publicDocsUrl } from "@/lib/docs";

interface SuccessScreenProps {
  tieneP12?: boolean;
  apiKey?: string;
  bindToken?: string;
  companyPublicId?: string;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  }
}

export default function SuccessScreen({
  tieneP12 = false,
  apiKey = "",
  bindToken = "",
  companyPublicId = "",
}: SuccessScreenProps) {
  const canBind = Boolean(bindToken && companyPublicId);
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(apiKey);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const NEXT_STEPS = [
    {
      icon: <Key size={16} />,
      title: tieneP12 ? "Certificado P12 cargado" : "Cargar certificado P12",
      desc: tieneP12 ? "Ya subiste tu certificado correctamente." : "Necesario para emitir en producción.",
      href: publicDocsUrl("#cargar-p12"),
      completed: tieneP12
    },
    { icon: <Settings size={16} />, title: "Cargar rangos NCF", desc: "Asigna los rangos de NCF por tipo.", href: publicDocsUrl("#rangos-ncf"), completed: false },
    { icon: <ArrowRight size={16} />, title: "Primer e-CF en TesteCF", desc: "Emite tu primer comprobante de prueba.", href: publicDocsUrl("#post-ecf"), completed: false },
    { icon: <BookOpen size={16} />, title: "Leer la documentación", desc: "Guías de integración y referencia API.", href: publicDocsUrl(), completed: false },
  ];

  return (
    <div className="text-center space-y-6">
      {/* Animated check */}
      <div className="flex justify-center">
        <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full animate-fade-in">
          <Check size={36} className="text-emerald-600" strokeWidth={2.5} />
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Empresa creada con éxito!</h2>
        <p className="text-slate-500 text-sm max-w-sm mx-auto">
          Tu cuenta emite.do está lista. Guarda tu API Key en un lugar seguro — se muestra una sola vez.
        </p>
      </div>

      {/* API Key */}
      <div className="bg-slate-950 rounded-2xl p-4 text-left">
        <p className="text-xs text-slate-500 mb-2 font-medium uppercase tracking-widest">Tu API Key</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs font-mono text-emerald-400 break-all select-all">
            {revealed ? apiKey : `${apiKey.slice(0, 16)}•••••••••••••••••••••••••`}
          </code>
          <button
            onClick={() => setRevealed(!revealed)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors cursor-pointer px-2 py-1.5 bg-slate-800 rounded-lg whitespace-nowrap"
            title={revealed ? "Ocultar" : "Mostrar"}
          >
            {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors cursor-pointer px-3 py-1.5 bg-slate-800 rounded-lg whitespace-nowrap"
          >
            {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
            {copied ? "Copiado" : "Copiar"}
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-left">
        <p className="text-xs text-amber-700">
          <strong>Aviso importante:</strong> Esta API Key no se volverá a mostrar. Guárdala en tu gestor de contraseñas o en las variables de entorno de tu aplicación.
        </p>
      </div>

      {/* Next steps */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-3 text-left">Siguientes pasos</p>
        <div className="grid grid-cols-2 gap-3">
          {NEXT_STEPS.map((step) => (
            <a
              key={step.title}
              href={step.href}
              className={`flex items-start gap-3 border rounded-xl p-3 transition-colors cursor-pointer text-left group ${
                step.completed
                  ? "bg-emerald-50/40 border-emerald-100 hover:bg-emerald-50 hover:border-emerald-200"
                  : "bg-slate-50 border-slate-100 hover:bg-blue-50 hover:border-blue-100"
              }`}
            >
              <span className={`w-7 h-7 flex items-center justify-center rounded-lg border flex-shrink-0 transition-colors ${
                step.completed
                  ? "bg-emerald-100 border-emerald-200 text-emerald-600"
                  : "bg-white border-slate-200 text-blue-800 group-hover:bg-blue-800 group-hover:text-white"
              }`}>
                {step.icon}
              </span>
              <div>
                <p className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  {step.title}
                  {step.completed && <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-medium">Listo</span>}
                </p>
                <p className="text-xs text-slate-500">{step.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        {canBind ? (
          <form method="POST" action={saasAppUrl("/api/onboarding/bind")}>
            <input type="hidden" name="bind_token" value={bindToken} />
            <input type="hidden" name="company_public_id" value={companyPublicId} />
            <button
              type="submit"
              className="inline-flex items-center gap-2 bg-blue-800 text-white font-semibold px-6 py-3 rounded-full hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Ir a la consola
              <ArrowRight size={16} />
            </button>
          </form>
        ) : (
          <a
            href={saasAppUrl("/sign-in")}
            className="inline-flex items-center gap-2 bg-blue-800 text-white font-semibold px-6 py-3 rounded-full hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Ir a la consola
            <ArrowRight size={16} />
          </a>
        )}
        <a
          href={publicDocsUrl()}
          className="inline-flex items-center gap-2 text-slate-600 font-medium px-4 py-3 hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap"
        >
          Ir a la documentación
        </a>
      </div>
    </div>
  );
}
